import { useEffect, useState } from "react";
import { RoleShell } from "@/components/RoleShell";
import { Crown, Star, Gem, Check, Users, TrendingUp } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import "@/components/membership.css";

const ICONS = { silver: Star, gold: Crown, platinum: Gem };

export default function Memberships() {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/membership-plans"), api.get("/membership-plans/stats")])
      .then(([p, s]) => {
        setPlans(unwrap(p) || []);
        setStats(unwrap(s));
      });
  }, []);

  return (
    <RoleShell title="Memberships">
      <div className="max-w-[1400px] mx-auto px-6 pb-12" data-testid="memberships-root">
        <div className="overline mb-2">Membership tiers</div>
        <h1 className="font-display font-bold text-[28px] tracking-tight mb-1">Choose how to reward your loyalists</h1>
        <p className="font-inter text-[14px] text-[var(--color-text-muted)] mb-6">
          Three preset tiers. Edit benefits anytime — they apply instantly to new enrolments.
        </p>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-7">
            <StatTile label="Total Active Members" value={stats.total_active} accent="#5E4060" Icon={Users} />
            <StatTile label="Monthly Recurring Revenue" value={`₹${Math.round(stats.total_mrr).toLocaleString("en-IN")}`} accent="#B5892E" Icon={TrendingUp} mono />
            <StatTile label="New this month" value={stats.new_this_month} accent="#15803D" />
            <StatTile label="Churned this month" value={stats.churned_this_month} accent="#B91C1C" />
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p) => <PlanCard key={p.id} plan={p} />)}
        </div>

        {/* Tier breakdown */}
        {stats && stats.by_tier && (
          <>
            <div className="overline mt-10 mb-3">Members by tier</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {stats.by_tier.map((t) => {
                const Icon = ICONS[t.color_scheme] || Crown;
                return (
                  <div key={t.plan_id} className="glass-primary rounded-2xl p-5"
                       data-testid={`tier-stat-${t.color_scheme}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Icon size={18} className="text-[var(--color-gold-deeper)]" />
                        <span className="font-sora font-semibold text-[14px]">{t.name}</span>
                      </div>
                      <span className={`tier-chip tier-chip-${t.color_scheme}`}>{t.badge_label}</span>
                    </div>
                    <div className="font-mono text-[28px] text-[var(--color-text-primary)] leading-none">{t.active}</div>
                    <div className="font-mono text-[12px] text-[var(--color-text-muted)] mt-1">members · ₹{Math.round(t.mrr).toLocaleString("en-IN")}/month MRR</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </RoleShell>
  );
}

function PlanCard({ plan }) {
  const Icon = ICONS[plan.color_scheme] || Crown;
  return (
    <div className={`plan-card plan-${plan.color_scheme}`} data-testid={`plan-card-${plan.color_scheme}`}>
      {plan.color_scheme === "gold" && <span className="plan-popular">Most popular</span>}
      <div className="flex items-center gap-2">
        <Icon size={20} />
        <span className="font-sora font-bold text-[16px] tracking-wider uppercase">{plan.badge_label}</span>
      </div>
      <div>
        <div className="font-sora font-bold text-[24px] tracking-tight">{plan.name}</div>
        <div className="font-mono text-[14px] mt-1">₹{plan.monthly_fee}/month or ₹{plan.annual_fee}/year</div>
        {plan.monthly_fee * 12 > plan.annual_fee && (
          <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full text-[10.5px] font-mono font-semibold"
                style={{ background: "rgba(34,197,94,0.16)", color: "#15803D" }}>
            Save ₹{plan.monthly_fee * 12 - plan.annual_fee} annually
          </span>
        )}
      </div>
      <ul className="space-y-1.5 flex-1">
        {plan.benefits.map((b, i) => (
          <li key={i} className="plan-checklist-li">
            <Check size={13} /> <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="font-mono text-[11px] opacity-75">
        {plan.active_members || 0} active member{plan.active_members === 1 ? "" : "s"}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent, mono, Icon }) {
  return (
    <div className="glass-primary rounded-2xl p-4">
      <div className="flex items-center gap-1.5 overline mb-2">
        {Icon && <Icon size={10} />}
        {label}
      </div>
      <div className={`font-mono text-[26px] leading-none`} style={{ color: accent }}>{value}</div>
    </div>
  );
}
